import type { FC, ReactNode } from "react"
import styles from "./SelectionToolbar.module.scss"

export type TToolbarAction = {
	key: string
	label: string
	icon: ReactNode
	/** Render as an icon-only square button (no visible label; label still used for a11y/title). */
	iconOnly?: boolean
	/** Draw a thin divider before this button (used to split the primary action from the icon group). */
	separatorBefore?: boolean
	disabled?: boolean
	title?: string
	onSelect: () => void
}

type Props = {
	actions: TToolbarAction[]
}

/**
 * Dark floating pill shown above a furniture selection (single or batch). Presentational only —
 * the host (useQuotationViewer) owns positioning inside the shadow-DOM overlay, builds the
 * contextual action set, and wires each `onSelect`. Matches the `QuotationContextMenu` dark tokens.
 */
export const SelectionToolbar: FC<Props> = ({ actions }) => {
	return (
		<div className={styles.toolbar} role="toolbar" data-testid="selection-toolbar">
			{actions.map((action) => (
				<span key={action.key} className={styles.slot}>
					{action.separatorBefore ? <span className={styles.separator} aria-hidden /> : null}
					<button
						type="button"
						className={`${styles.button} ${action.iconOnly ? styles.iconOnly : ""}`}
						disabled={action.disabled}
						title={action.title ?? action.label}
						aria-label={action.label}
						onClick={action.onSelect}
					>
						<span className={styles.icon}>{action.icon}</span>
						{action.iconOnly ? null : <span className={styles.label}>{action.label}</span>}
					</button>
				</span>
			))}
		</div>
	)
}
